/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/*
 * Shared String utility functions
 */
import isNil from 'lodash/isNil';
import {NetworkOrigin, TerminalDetails} from '../shared/entities/terminal';
import {bpsToUnitString} from './DataRateUtils';
import {terminalTypeDisplayNameFromTerminalTypeLabel} from './TerminalUtilities';

/*
 * Truncation.
 *
 * Accepts a string and if the string is longer than the truncateAt parameter, returns a string in the format of
 *
 *      s(0)s(1)s(2)...s(length - 3)s(length - 2)s(length - 3)
 *
 *  Example:
 *    Truncate at: 10
 *    N1234567-XYZ987 -> N12...Z987
 */

export const MINIMUM_LENGTH_TO_TRUNCATE = 7;

export const truncateMiddle = (
  valueToTrunc: string | undefined,
  truncateAt: number = MINIMUM_LENGTH_TO_TRUNCATE + 1,
) => {
  if (valueToTrunc === undefined || valueToTrunc === null) {
    return '';
  }

  return valueToTrunc.length < truncateAt || truncateAt < MINIMUM_LENGTH_TO_TRUNCATE
    ? valueToTrunc
    : `${valueToTrunc.slice(0, (truncateAt - 3) / 2)}...${valueToTrunc.slice(
        valueToTrunc.length - (truncateAt - 3) / 2,
        valueToTrunc.length,
      )}`;
};

/**
 * Returns the value or a string placeholder if the value is Nil.
 * Optional: wrapper Function to invoke if the value is not Nil
 */
export const EMPTY_FIELD_STRING_PLACEHOLDER = '- -';

export const valueOrPlaceholder = (valueToCheck: any, formattingFunction?: Function): string => {
  if (isNil(valueToCheck) || valueToCheck === '') {
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  }
  return !isNil(formattingFunction) ? formattingFunction(valueToCheck) : valueToCheck;
};

// Takes in the network name and abbreviates it
export const abbreviateNetworkName = (networkName: NetworkOrigin) => {
  let abbreviatedNetworkName: string = networkName;
  if (!isNil(networkName)) {
    switch (networkName) {
      case 'Arclight-1':
        abbreviatedNetworkName = 'AL';
        break;
      case 'Viasat':
        abbreviatedNetworkName = 'VI';
        break;
      case 'ViaSat-3':
        abbreviatedNetworkName = 'VS3';
        break;
      default:
        break;
    }
  }
  return abbreviatedNetworkName;
};
// Removes the 1 from Arclight and displays "ArcLight" and transforms any case of "viasat" to "Viasat"
export const transformNetworkName = (networkName: NetworkOrigin) => {
  let transformedNetworkName: String = networkName;
  if (!isNil(networkName)) {
    switch (networkName) {
      case 'Arclight-1':
        transformedNetworkName = 'ArcLight';
        break;
      case 'Viasat':
        transformedNetworkName = 'VI';
        break;
      case 'ViaSat-3':
        transformedNetworkName = 'VS3';
        break;
      default:
        break;
    }
  }
  return transformedNetworkName;
};

// Transforms CRL to PRL
export const transformRLWaveformName = (rawWaveformName: string) => {
  let transformedWaveformName = rawWaveformName;
  if (!isNil(rawWaveformName)) {
    if (rawWaveformName.toLocaleLowerCase().replace(/ /g, '') === 'crl') {
      transformedWaveformName = 'PRL';
    }
  }
  return transformedWaveformName;
};

// transforms true or false to yes or no or string placeholder (by default)
export const booleanToAliasOrPlaceholder = (
  // string to return if rawValue is true
  trueString: string,
  // string to return if rawValue is false
  falseString: string,
  // string to return if rawValue is undefined or null.
  // * Defaults to the '--' string placeholder
  nullOrUndefinedString: string = EMPTY_FIELD_STRING_PLACEHOLDER,
) => {
  return (rawValue: boolean | undefined | null) => {
    let label = nullOrUndefinedString;
    if (!isNil(rawValue) && rawValue) {
      label = trueString;
    } else if (!isNil(rawValue) && !rawValue) {
      label = falseString;
    }
    return label;
  };
};

/**
 * Takes in an sla and returns a string as a human readable range or lower / upper
 *
 * @param slaLowerBoundBps the upper bound of the SLA in bps
 * @param slaUpperBoundBps the lower bound of the SLA in bps
 */
export const formatSlaForDisplay = (slaLowerBoundBps?: number, slaUpperBoundBps?: number): string => {
  // Returns
  if (!isNil(slaLowerBoundBps) && !isNil(slaUpperBoundBps)) {
    return `${bpsToUnitString(slaLowerBoundBps, 0, 1)} to ${bpsToUnitString(slaUpperBoundBps, 0, 1)}`;
  } else if (!isNil(slaUpperBoundBps)) {
    return `${bpsToUnitString(slaUpperBoundBps, 0, 1)}`;
  } else {
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  }
};

export const arrayIndexToLetter = (index: number) => {
  switch (index) {
    case 0:
      return 'A';
    case 1:
      return 'B';
    case 2:
      return 'C';
    case 3:
      return 'D';
    case 4:
      return 'E';
    case 5:
      return 'F';
    case 6:
      return 'G';
    case 7:
      return 'H';
    default:
      return '';
  }
};

export const sortAlphabetically = (list: string[]) => {
  list.sort((a, b) => a.localeCompare(b));
  return list;
};

/**
 * Get the formatter function for fixing number values to certain decimal place.
 * @param toFixedDecimal the number of decimal places to truncate the value to.
 * @param suffix the suffix to be added after the fixed value.
 */
export const toFixedValueFormatter = (toFixedDecimal: number, suffix: string = '') => {
  return (val: number | undefined | null) => {
    if (!isNil(val)) {
      return `${val.toFixed(toFixedDecimal)}${suffix}`;
    }
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  };
};

export const getTerminalTypeString = (terminal: TerminalDetails | undefined) => {
  let terminalTypeString = '';
  if (terminal) {
    terminalTypeString = terminalTypeDisplayNameFromTerminalTypeLabel(terminal.terminalTypeLabel) || '';
  }
  return terminalTypeString ? terminalTypeString : null;
};

/**
 * Converts str to Title Case (first letter of each word is capitalized)
 * @param str string to convert to title case
 */
export const toTitleCase = (str: string) => {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}
